import { useFormContext, useWatch } from 'react-hook-form';
import { ConfirmationStep } from 'pages/AppointmentsReserve/components/steps/ConfirmationStep';
import { DataStep } from 'pages/AppointmentsReserve/components/steps/DataStep';
import { PsychologistStep } from 'pages/AppointmentsReserve/components/steps/PsychologistStep';
import { SearchPatientStep } from 'pages/AppointmentsReserve/components/steps/SearchPatient';
import { SessionStep } from 'pages/AppointmentsReserve/components/steps/SessionStep';
import { AppointmentsReservePayload, FLOW_STEP_NAMES } from 'pages/AppointmentsReserve/constants';
import {
  type AppointmentsReserveFormData,
  type ReserveFlow,
  type StepProps,
  type Steps,
} from 'pages/AppointmentsReserve/types';

export type StepDefinition = {
  id: number;
  name: Steps;
  component: React.ComponentType<StepProps>;
};

const STEP_COMPONENTS: Record<Steps, React.ComponentType<StepProps>> = {
  patient: SearchPatientStep,
  data: DataStep,
  session: SessionStep,
  confirmation: ConfirmationStep,
  facultative: PsychologistStep,
};

const STEPS_BY_FLOW = Object.fromEntries(
  Object.entries(FLOW_STEP_NAMES).map(([flow, stepNames]) => [
    flow,
    stepNames.map<StepDefinition>((name, index) => ({
      id: index + 1,
      name,
      component: STEP_COMPONENTS[name],
    })),
  ])
) as Record<ReserveFlow, StepDefinition[]>;

interface UseStepNavigationResult {
  steps: StepDefinition[];
  currentStepIndex: number;
  currentStep: StepDefinition;
  handleNext: () => void;
  handleBack: () => void;
}

export const useStepNavigation = (): UseStepNavigationResult => {
  const { control, setValue } = useFormContext<AppointmentsReserveFormData>();
  const [flow, currentStepName] = useWatch({
    control,
    name: [AppointmentsReservePayload.flow, AppointmentsReservePayload.currentStepName],
  });
  const steps = STEPS_BY_FLOW[flow as ReserveFlow];

  const foundStepIndex = steps.findIndex(step => step.name === currentStepName);
  const currentStepIndex = Math.max(foundStepIndex, 0);
  const currentStep = steps[currentStepIndex];

  const handleNext = (): void => {
    const nextIndex = Math.min(currentStepIndex + 1, steps.length - 1);
    setValue(AppointmentsReservePayload.currentStepName, steps[nextIndex].name);
  };

  const handleBack = (): void => {
    const previousIndex = Math.max(currentStepIndex - 1, 0);
    setValue(AppointmentsReservePayload.currentStepName, steps[previousIndex].name);
  };

  return {
    steps,
    currentStepIndex,
    currentStep,
    handleNext,
    handleBack,
  };
};
